import UserAvatar from './UserAvatar'
import { useTranslation } from 'react-i18next'
import { User } from '@shared-types/common'

interface AvatarStackProps {
  users: User[]
  maxVisible?: number
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

export default function AvatarStack({ users, maxVisible = 3, size = 'sm' }: AvatarStackProps) {
  const { t: _t } = useTranslation()

  if (users.length === 0) {
    return <span className="text-xs text-base-content/50">-</span>
  }

  const visibleUsers = users.slice(0, maxVisible)
  const remainingUsers = users.slice(maxVisible)
  const remainingCount = remainingUsers.length

  // Size classes for the +N indicator
  const sizeClass = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  }[size]

  const getDisplayName = (user: User): string => {
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email
  }

  return (
    <div className=" -space-x-3">
      {visibleUsers.map((user) => (
        <UserAvatar key={user.id} user={user} size={size} />
      ))}
      {remainingCount === 1 ? (
        // Show the avatar directly when there's only 1 remaining user
        <UserAvatar user={remainingUsers[0]} size={size} />
      ) : remainingCount > 1 ? (
        // Show tooltip for 2+ remaining users
        <div
          className="tooltip tooltip-top avatar"
          data-tip={remainingUsers.map(user => getDisplayName(user)).join(', ')}
        >
          <div className="placeholder">
            <div className={`bg-base-300 text-base-content rounded-full flex items-center justify-center ${sizeClass}`}>
              <span>+{remainingCount}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
